import { Box, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AlgoliaHit } from 'instantsearch.js';
import { useAtomValue } from 'jotai';
import { Contract_Status_Enum } from '@kheops/graphql';
import { SupplierHit } from '../search';
import { RoutePaths } from '../../routes/AppRoutes';
import ContractStatusTag from '../ContractStatusTag';
import SizedImage from '../../common/components/SizedImage';
import CompanyDistanceChip from '../../common/components/CompanyDistanceChip';
import { DiscoveryWordingsKeys } from '../discovery.utils';
import ImageDarkOverlay from '../../common/components/ImageDarkOverlay';
import SearchUtils from '../../common/utils/search.utils';
import { currentContextAtom } from '../../state';
import { PackagingFamilyTypeIntl } from '../../common/i18n/product-family.translation';
import { ellipsisStyle } from '../../common/utils/style.utils';
import CompanyLogo from '../../common/components/CompanyLogo';
import FileUtils from '../../common/utils/file.utils';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface SearchBuyerHitProps {
  hit: AlgoliaHit<SupplierHit>;
}

export default function SearchBuyerHit({ hit }: SearchBuyerHitProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'discovery', 'product-family']);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { companyId, deliveryZones } = useAtomValue(currentContextAtom);

  const canDeliver = useMemo(() => {
    if (!hit.partners.includes(`${companyId}_SIGNED`) && deliveryZones?.length) {
      const hitAdministrativeArea2 = hit.administrative_areas.find((administrativeArea) => administrativeArea.includes('administrative_area_level_2'))?.split('|')[1];

      return SearchUtils.canItDeliverAnArea(deliveryZones, hitAdministrativeArea2!);
    }

    return true;
  }, [hit]);

  const contractStatusBadge = useMemo(() => {
    const partnerInfo = hit.partners.find((partner) => partner.includes(companyId));

    if (!partnerInfo) {
      return undefined;
    }

    return (
      <ContractStatusTag status={partnerInfo.split('_')[1] as Contract_Status_Enum} />
    );
  }, []);

  const handleBuyerHitClick = useCallback((): void => {
    const routePath = RoutePaths.DISCOVERY_BUYER;

    navigate(
      generatePath(routePath, { companyId: hit.objectID }),
      {
        state: {
          origin: 'DISCOVERY',
        },
      },
    );
  }, []);

  return (
    <Box
      onClick={handleBuyerHitClick}
      sx={{
        cursor: 'pointer',
        display: 'flex',
        flexDirection: {
          xs: 'row',
          sm: 'column',
        },
        mb: 1.5,
      }}
    >
      <SizedImage
        src={FileUtils.BuildCompanyImageUrl({ photo: hit.photo, type: hit.business_profile })}
        alt={t('common:company')}
        width={{ xs: 120, sm: '100%' }}
        sx={{
          flexShrink: 0,
          backgroundColor: 'surfaceContainerLowest.main',
          borderRadius: 6,
          '& img:not([class^="Kheops-companyLogo"]):not([class*=" Kheops-companyLogo"])': {
            maxHeight: 'unset',
            aspectRatio: { xs: '1 / 1', sm: '4 / 3' },
            objectFit: 'cover',
          },
        }}
        overlay={(
          <>
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
                px: 1,
              }}
            >
              {!isXs && (
                <>
                  {/* _geoloc's type is wrong, it should also accept lon for longitude, as it is configured that way in our records. */}
                  <CompanyDistanceChip targetGeoLoc={{ lat: hit._geoloc!.lat, lng: (hit._geoloc! as any).lon }} />
                </>
              )}
              {hit.brand && (
                <CompanyLogo
                  brand={hit.brand}
                  size="medium"
                  variant="square"
                />
              )}
            </Box>

            {!canDeliver && (
              <ImageDarkOverlay label={t(DiscoveryWordingsKeys.SUPPLIER.out_of_delivery_zones)} />
            )}
          </>
        )}
      />
      <Box
        sx={{
          mt: {
            xs: 0,
            sm: 1,
          },
          ml: {
            xs: 1,
            sm: 0,
          },
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 0.5,
              pt: {
                xs: 1,
                sm: 0,
              },
              pl: {
                xs: 0,
                sm: 2,
              },
            }}
          >
            {contractStatusBadge}
            <Tooltip title={hit.tradeName} enterDelay={500} enterNextDelay={500}>
              <Typography
                variant="bodyLarge"
                sx={{
                  ml: contractStatusBadge ? 0.5 : 0,
                  ...ellipsisStyle,
                }}
              >
                {hit.tradeName}
              </Typography>
            </Tooltip>
          </Box>
          {!!hit.product_families?.length && (
            <Typography variant="bodyMedium" color="secondary">
              {t(`product-family:${PackagingFamilyTypeIntl[hit.product_families[0]]}`)}
              {hit.product_families.length > 1 && ` (+${hit.product_families.length - 1})`}
            </Typography>
          )}
        </Box>
        {isXs && (
          <CompanyDistanceChip targetGeoLoc={{ lat: hit._geoloc!.lat, lng: (hit._geoloc! as any).lon }} />
        )}
      </Box>
    </Box>
  );
}
